import * as _ from "underscore";
import metaCache from "flow/utils/metaCache";
import config from "flow/config";
import flowBaseUrl from "flow/wrappers/baseUrl";
import { Dictionary } from "flow/api/types";
import { ModuleRole } from "flow/entities/ModuleRole";
import { CommandManifest } from "flow/entities/CommandManifest";
import { LicenseBundle } from "flow/entities/LicenseBundle";
import ToolbarItemManifestEx from "flow/manifests/ToolbarItemManifestEx";
import { ImpersonatedUser } from "flow/entities/ImpersonatedUser";

export interface IRole {
    name: string;
    roles: ModuleRole[];
    isRoleSelectionExclusive: boolean;
    onDemandOnly: boolean;
    isEmpty: boolean;
    isBroken: boolean;
    inactiveReason: string;
}

export interface IWebModule {
    id: string;
    name: string;
    url: string;
    authorizationManager: any;
    moduleKeyName: string;
    onDemandOnly: boolean;
    isEmpty: boolean;
    isBroken: boolean;
}

export interface IWebModuleLite {
    id: string;
    name: string;
    description: string;
}

export interface IUserContextTabManifestLite {
    id: string;
    name: string;
    icon: string;
}

export interface ISideTabManifestLite {
    id: string;
    name: string;
    icon: string;
}

let webModules: IWebModule[] = [];
let roleBlacklist: string[] = [];
let roleTree: Dictionary<IRole> = {};
let manifests: Dictionary<any> = {};
let commandScripts: CommandManifest[] = [];
let licenseBundles: LicenseBundle[] = [];
let impersonatedUser: ImpersonatedUser;
const cacheBusters: Dictionary<string> = {};

const moduleDescriptions: Dictionary<string> = {};

function clear(): void {
    webModules = [];
    roleBlacklist = [];
    roleTree = {};
    manifests = {};
    commandScripts = [];
    impersonatedUser = undefined;
}

function setBlacklist(blacklist: string[]): void {
    roleBlacklist = blacklist;
}

function getBlacklist(): string[] {
    return roleBlacklist;
}

function deleteManifest(id: string): void {
    delete manifests[id];
}

function forceSingleModule(moduleId: string) {
    // Remove module from blacklist if present
    const index = roleBlacklist.findIndex(r => r === moduleId);
    if (index >= 0) {
        roleBlacklist.splice(index, 1);
    }

    // Hide module selector
    deleteManifest(config.userContextTabs.moduleSelector);
}

function addRoles(
    moduleId: string,
    moduleName: string,
    subModuleRoles: any,
    isRoleSelectionExclusive: boolean,
    onDemandOnly: boolean
): void {
    roleTree[moduleId] = {
        name: moduleName,
        roles: subModuleRoles, // id, name pairs
        isRoleSelectionExclusive: isRoleSelectionExclusive,
        onDemandOnly: onDemandOnly,
        isEmpty: true,
        isBroken: false,
        inactiveReason: undefined
    };
}

function flagModuleAsBroken(moduleId: string): void {
    roleTree[moduleId].isBroken = true;

    const webModule = webModules.find(wm => wm.id === moduleId);
    if (webModule) {
        webModule.isBroken = true;
    }
}

function addImpersonatedUser(_impersonatedUser: any): void {
    impersonatedUser = _impersonatedUser;
}

function addWebModule(
    moduleId: string,
    moduleName: string,
    url: string,
    roles: ModuleRole[],
    authorizationManager: any,
    isRoleSelectionExclusive: boolean,
    _impersonatedUser: any,
    moduleKeyName: string,
    onDemandOnly: boolean,
    cacheBuster: string
): void {
    // Remove tentative entry if any
    const existingModule = webModules.find(module => module.id === moduleId);
    if (existingModule) {
        webModules.splice(webModules.indexOf(existingModule), 1);
    }

    addRoles(moduleId, moduleName, roles, isRoleSelectionExclusive, onDemandOnly);
    if (
        _impersonatedUser !== undefined &&
        _impersonatedUser.personId !== null &&
        _impersonatedUser.personId !== undefined
    ) {
        addImpersonatedUser(_impersonatedUser);
    }

    webModules.push({
        id: moduleId,
        name: moduleName,
        url: url,
        authorizationManager: authorizationManager,
        moduleKeyName: moduleKeyName,
        onDemandOnly: onDemandOnly,
        isEmpty: false,
        isBroken: false
    });

    cacheBusters[moduleId] = cacheBuster;
}

function setDescription(moduleId: string, moduleDescription: string): void {
    moduleDescriptions[moduleId] = moduleDescription;
}

function getModuleList(): IWebModuleLite[] {
    return webModules.map(wm => {
        return {
            id: wm.id,
            name: wm.name,
            description: moduleDescriptions[wm.id] || "<missing>"
        };
    });
}

function addManifests(moduleId: string, newManifests: any[], allowResizeComponents?: boolean) {
    const baseUrl = getWebModuleUrl(moduleId);
    const moduleKeyName = getWebModuleKeyName(moduleId);

    if (newManifests.length > 0) {
        roleTree[moduleId].isEmpty = false;
    }

    newManifests.forEach(function (manifest: any) {
        decorateManifest(baseUrl, moduleId, manifest, moduleKeyName);
        translateNameAndTooltip(manifest);
        decorateToolbarItemWithId(manifest);
        decorateToolbarItemWithVisibilityLogic(manifest);
        manifest.allowResizeComponents = allowResizeComponents;
        manifests[manifest.id] = manifest;
    });
}

function flagWebModuleAsEmpty(moduleId: string) {
    const webModule = webModules.find(wm => wm.id === moduleId);
    if (webModule) {
        webModule.isEmpty = true;
    }
}

function flagWebModuleAsInactive(moduleId: string, inactiveReason: string) {
    const webModule = roleTree[moduleId];
    if (webModule) {
        webModule.inactiveReason = inactiveReason;
    }
}

function getWebModuleKeyName(moduleId: string): string {
    const webModule = webModules.find(module => {
        return module.id == moduleId;
    });
    return webModule.moduleKeyName;
}

function addCommandScripts(newScripts: CommandManifest[]): void {
    if (newScripts) {
        commandScripts = commandScripts.concat(newScripts);
    }
}

function getAllCommandScripts(): CommandManifest[] {
    return commandScripts;
}

function getWebModuleUrl(moduleId: string): string {
    const webModule = webModules.find(module => {
        return module.id == moduleId;
    });
    return webModule.url;
}

function getWebModuleUrls(): string[] {
    return webModules.filter(wm => !isExcludedWebModule(wm.id) && !wm.isEmpty && !wm.isBroken).map(wm => wm.url);
}

function addLicenseBundles(newLicenseBundles: any[]): void {
    if (newLicenseBundles) {
        licenseBundles = licenseBundles.concat(newLicenseBundles);
    }
}

function getAllLicenseBundles(): LicenseBundle[] {
    return licenseBundles;
}

function decorateManifest(baseUrl: string, moduleId: string, manifest: any, moduleKeyName: string): void {
    manifest.baseUrl = baseUrl;
    manifest.moduleId = moduleId;
    manifest.moduleKeyName = moduleKeyName;

    if (hasToolbarItem(manifest) && !isToolbarIconLcIcon(manifest)) {
        manifest.toolbarItem.icon = manifest.baseUrl + manifest.toolbarItem.icon;
    }

    decorateCssUrls(baseUrl, manifest.cssUrls);

    if (!manifest.moduleRoles) {
        manifest.moduleRoles = [];
    }
}

function decorateCssUrls(baseUrl: string, cssUrls: string[]): void {
    if (cssUrls) {
        for (let i = 0; i < cssUrls.length; i++) {
            cssUrls[i] = baseUrl + cssUrls[i];
        }
    }
}

function translateNameAndTooltip(manifest: any): void {
    manifest.name = metaCache.lookup(manifest.name, manifest.name);

    if (hasToolbarItem(manifest)) {
        manifest.toolbarItem.tooltip = metaCache.lookup(manifest.toolbarItem.tooltip, manifest.toolbarItem.tooltip);
    }
}

function decorateToolbarItemWithId(manifest: any): void {
    if (hasToolbarItem(manifest)) {
        manifest.toolbarItem.id = manifest.id;
    }
}

function decorateToolbarItemWithVisibilityLogic(manifest: any): void {
    if (hasToolbarItem(manifest)) {
        manifest.toolbarItem.shouldBeVisible = function (contextId: string) {
            if (manifest.toolbarItem.contextIds.length === 0) {
                return true;
            }

            return _.contains(manifest.toolbarItem.contextIds, contextId);
        };
    }
}

function getManifestWithId(id: string): any {
    if (manifests.hasOwnProperty(id)) {
        return manifests[id];
    }
    return null;
}

function getGroupMenuManifests(functionType: string): any {
    const result = [];
    for (const id in manifests) {
        if (
            manifests[id].groupMenuItem != undefined &&
            manifests[id].groupMenuItem != null &&
            manifests[id].groupMenuItem.functionType === functionType &&
            !isFiltered(id)
        ) {
            result.push(manifests[id]);
        }
    }

    return result;
}

function getCustomGroupMenuManifests(functionType: string): any {
    const result = [];
    for (const id in manifests) {
        if (
            manifests[id].customGroupMenuItem != undefined &&
            manifests[id].customGroupMenuItem != null &&
            manifests[id].customGroupMenuItem.functionType === functionType &&
            !isFiltered(id)
        ) {
            result.push(manifests[id]);
        }
    }

    return result;
}

function getHomeScreenManifests(): any {
    const homeManifests = [];
    for (const id in manifests) {
        if (manifests[id].isVisibleOnHomeScreen === true && !isFiltered(id)) {
            homeManifests.push(manifests[id]);
        }
    }

    return homeManifests;
}

function getConfigItemManifests(): any {
    const configItemManifests = [];
    for (const id in manifests) {
        if (manifests[id].configItem != undefined && manifests[id].configItem != null && !isFiltered(id)) {
            configItemManifests.push(manifests[id]);
        }
    }
    return configItemManifests;
}

function getAutoStartMenuManifests(): any {
    const menuManifests = [];
    for (const id in manifests) {
        if (manifests[id].isMenu === true && manifests[id].isAutoStart === true && !isFiltered(id)) {
            menuManifests.push(manifests[id]);
        }
    }

    return menuManifests;
}

function hasNavigationPanelItem(id: string): boolean {
    return hasToolbarItem(manifests[id]);
}

function getNavigationPanelItems(): ToolbarItemManifestEx[] {
    let items = [];
    for (const id in manifests) {
        if (hasToolbarItem(manifests[id]) && !isFiltered(id)) {
            items.push(manifests[id].toolbarItem);
        }
    }
    items = _.sortBy(items, function (item: any) {
        return item.order;
    });
    return items;
}

let uctCounter = 0;
function getUserContextTabManifests(): IUserContextTabManifestLite[] {
    const items = [];

    for (const id in manifests) {
        if (isUserContextTab(id) && !isFiltered(id)) {
            items.push({
                id: id,
                name: manifests[id].name,
                icon: manifests[id].icon,
                order: manifests[id].order || 10000 + uctCounter++
            });
        }
    }

    items.sort((a, b) => a.order - b.order);

    return items;
}

function getSideTabManifests(): ISideTabManifestLite[] {
    const items = [];

    for (const id in manifests) {
        if (isSideTab(id) && !isFiltered(id)) {
            items.push({ id: id, name: manifests[id].name, icon: manifests[id].icon });
        }
    }

    return items;
}

function hasToolbarItem(manifest: any): boolean {
    return manifest.toolbarItem !== null && manifest.toolbarItem !== undefined;
}

function isToolbarIconLcIcon(manifest: any): boolean {
    return (
        manifest.toolbarItem.icon !== null &&
        manifest.toolbarItem.icon !== undefined &&
        manifest.toolbarItem.icon.indexOf("lc-icon") !== -1
    );
}

function isWorkingContext(id: string): boolean {
    const manifest = getManifestWithId(id);
    return manifest !== null && manifest !== undefined && manifest.isWorkingContext === true;
}

function isDialog(id: string): boolean {
    const manifest = getManifestWithId(id);
    return manifest !== null && manifest !== undefined && manifest.isDialog === true;
}
function isMenu(id: string): boolean {
    const manifest = getManifestWithId(id);
    return manifest !== null && manifest !== undefined && manifest.isMenu === true;
}
function isHome(id: string): boolean {
    return id === config.componentIds.homeContext;
}

function isUserContextTab(id: string): boolean {
    const manifest = getManifestWithId(id);
    return manifest !== null && manifest !== undefined && manifest.isUserContextTab === true;
}

function isSideTab(id: string): boolean {
    const manifest = getManifestWithId(id);
    return manifest !== null && manifest !== undefined && manifest.isSideTab === true;
}

function isComponent(id: string): boolean {
    return !(isWorkingContext(id) || isDialog(id) || isMenu(id) || isHome(id) || isUserContextTab(id) || isSideTab(id));
}

function isHomeScreenComponent(id: string): boolean {
    const manifest = getManifestWithId(id);
    return manifest !== null && manifest !== undefined && isComponent(id) && manifest.isVisibleOnHomeScreen === true;
}

function isAppScreenComponent(id: string): boolean {
    // TODO Remove if noone uses it anymore
    return isComponent(id) && !isHomeScreenComponent(id) && !isExternalComponent(id);
}

function isFunctionComponent(id: string): boolean {
    const manifest = getManifestWithId(id);
    return (
        isComponent(id) &&
        !isHomeScreenComponent(id) &&
        !isExternalComponent(id) &&
        manifest.hasNoWorkingContext === true
    );
}

function isContextContainer(id: string): boolean {
    return isComponent(id) && !isHomeScreenComponent(id) && !isExternalComponent(id) && !isFunctionComponent(id);
}

function isExternalComponent(id: string): boolean {
    const manifest = getManifestWithId(id);
    return manifest !== null && manifest !== undefined && manifest.isExternalComponent === true;
}

function isExcludedWebModule(webModuleId: string): boolean {
    return _.contains(roleBlacklist, webModuleId);
}

function mightCauseNavigation(id: string): boolean {
    return isHome(id) || isWorkingContext(id) || isFunction(id);
}

function isFiltered(manifestId: string): boolean {
    const manifest = manifests[manifestId];
    if (manifest === undefined || manifest === null) {
        return false;
    }

    if (isExcludedWebModule(manifest.moduleId)) {
        return true;
    }

    const roles = manifest.moduleRoles;
    if (roles.length === 0) {
        return false;
    }

    return _.every(roles, function (role: any) {
        return _.contains(roleBlacklist, role);
    });
}

function getRoleTree(): Dictionary<IRole> {
    return roleTree;
}

function getImpersonatedUser(): ImpersonatedUser {
    return impersonatedUser;
}

function getSelectedSubRolesForWebModule(webModuleId: string): ModuleRole[] {
    const webModule = roleTree[webModuleId];

    if (!webModule) {
        return [];
    }

    if (_.contains(roleBlacklist, webModuleId)) {
        return [];
    }

    return _.filter(webModule.roles, function (role: any) {
        return !_.contains(roleBlacklist, role.id);
    });
}

function getWebModuleAuthorizationManager(webModuleId: string): any {
    return _.chain(webModules)
        .filter(function (wm: any) {
            return wm.id == webModuleId;
        })
        .pluck("authorizationManager")
        .value()[0];
}

function isAvailable(id: string): boolean {
    return this.getManifestWithId(id) != null;
}

addWebModule(
    config.moduleId,
    config.moduleName,
    flowBaseUrl,
    undefined,
    undefined,
    false,
    undefined,
    config.moduleKeyName,
    false,
    undefined
);

function isFunction(id: string): boolean {
    const manifest = getManifestWithId(id);
    return (
        manifest !== null &&
        manifest !== undefined &&
        isComponent(id) &&
        manifest.hasNoWorkingContext &&
        !isHomeScreenComponent(id) &&
        !isExternalComponent(id)
    );
}

function getOnDemandSharedModuleIds(): string[] {
    const sharedModuleIds = webModules
        .filter(function (wm) {
            return !isExcludedWebModule(wm.id) && wm.onDemandOnly != undefined && wm.onDemandOnly;
        })
        .map(function (m) {
            return m.id.toLowerCase();
        });
    return uniqueArray(sharedModuleIds);
}

function uniqueArray<T>(array: T[]): T[] {
    const arr: T[] = [];
    for (let i = 0; i < array.length; i++) {
        if (!arr.includes(array[i])) {
            arr.push(array[i]);
        }
    }
    return arr;
}

function getManifestIdsByTag(tag: string): string[] {
    const result = [];
    for (const id in manifests) {
        if (
            !isFiltered(id) &&
            manifests[id].tags != undefined &&
            manifests[id].tags != null &&
            manifests[id].tags.indexOf(tag) > -1
        ) {
            result.push(id);
        }
    }
    return result;
}

function isFlow3FunctionContainer(id: string): boolean {
    const manifest = getManifestWithId(id);
    return manifest !== null && manifest !== undefined && manifest.isFlow3Component === true;
}

function isFlow3Context(id: string): boolean {
    const manifest = getManifestWithId(id);
    return manifest !== null && manifest !== undefined && manifest.isFlow3Context === true;
}

function getCacheBusterForModule(moduleId: string): string {
    return cacheBusters[moduleId];
}

export default {
    clear: clear,
    setBlacklist: setBlacklist,
    getBlacklist: getBlacklist,
    forceSingleModule,
    addWebModule: addWebModule,
    setDescription: setDescription,
    getModuleList: getModuleList,
    flagModuleAsBroken: flagModuleAsBroken,
    flagWebModuleAsEmpty: flagWebModuleAsEmpty,
    flagWebModuleAsInactive: flagWebModuleAsInactive,
    addManifests: addManifests,
    addCommandScripts: addCommandScripts,
    getAllCommandScripts: getAllCommandScripts,
    getWebModuleUrls: getWebModuleUrls,
    addLicenseBundles: addLicenseBundles,
    getAllLicenseBundles: getAllLicenseBundles,
    getManifestWithId: getManifestWithId,
    getHomeScreenManifests: getHomeScreenManifests,
    getConfigItemManifests: getConfigItemManifests,
    getAutoStartMenuManifests: getAutoStartMenuManifests,
    getGroupMenuManifests: getGroupMenuManifests,
    getCustomGroupMenuManifests: getCustomGroupMenuManifests,
    hasNavigationPanelItem: hasNavigationPanelItem,
    getNavigationPanelItems: getNavigationPanelItems,
    isWorkingContext: isWorkingContext,
    isDialog: isDialog,
    isMenu: isMenu,
    isHome: isHome,
    isSideTab: isSideTab,
    isComponent: isComponent,
    isHomeScreenComponent: isHomeScreenComponent,
    isAppScreenComponent: isAppScreenComponent,
    isFunctionComponent: isFunctionComponent,
    isContextContainer: isContextContainer,
    isExternalComponent: isExternalComponent,
    isExcludedWebModule: isExcludedWebModule,
    mightCauseNavigation: mightCauseNavigation,
    isFiltered: isFiltered,
    isAvailable: isAvailable,
    getRoleTree: getRoleTree,
    getSelectedSubRolesForWebModule: getSelectedSubRolesForWebModule,
    getWebModuleAuthorizationManager: getWebModuleAuthorizationManager,
    isFunction: isFunction,
    getImpersonatedUser: getImpersonatedUser,
    isUserContextTab: isUserContextTab,
    getUserContextTabManifests: getUserContextTabManifests,
    getSideTabManifests: getSideTabManifests,
    getOnDemandSharedModuleIds: getOnDemandSharedModuleIds,
    getManifestIdsByTag: getManifestIdsByTag,
    isFlow3FunctionContainer: isFlow3FunctionContainer,
    isFlow3Context: isFlow3Context,
    getCacheBusterForModule: getCacheBusterForModule
};
